import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.js';
import { useStore } from '../state/store.js';
import { VideoThumb, Icons } from '../components/ui.jsx';
import { formatHms, formatMs, relativeTime } from '../lib/format.js';

export function RendersScreen() {
  const navigate = useNavigate();
  const toast = useStore((s) => s.toast);
  const [tab, setTab] = useState('all');
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, active: 0, done: 0, failed: 0, draft: 0 });
  const [selected, setSelected] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    api.listClips({ filter: tab, limit: 300 })
      .then((r) => {
        setItems(r.items || []);
        setSummary(r.summary || { total: 0, active: 0, done: 0, failed: 0, draft: 0 });
        setSelected((prev) => {
          const ids = new Set((r.items || []).map((c) => c.id));
          const next = new Set();
          for (const id of prev) if (ids.has(id)) next.add(id);
          return next;
        });
      })
      .catch((err) => toast(err.message, 'err'));
  }, [tab, toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const visibleIds = useMemo(() => items.map((c) => c.id), [items]);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = visibleIds.some((id) => selected.has(id));

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(visibleIds));
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const deleteIds = async (ids) => {
    if (!ids.length) return;
    const n = ids.length;
    const label = n === 1 ? 'этот клип' : `${n} клипов`;
    if (!window.confirm(`Удалить ${label}? Субтитры и рендеры будут удалены.`)) return;
    setDeleting(true);
    try {
      const results = await Promise.allSettled(ids.map((id) => api.deleteClip(id)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      if (failed) toast(`удалено ${n - failed} из ${n}, ошибок: ${failed}`, 'err');
      else toast(n === 1 ? 'клип удалён' : `удалено ${n}`, 'ok');
      setSelected(new Set());
      load();
    } catch (err) {
      toast(err.message, 'err');
    } finally {
      setDeleting(false);
    }
  };

  const bulkDelete = () => deleteIds([...selected]);

  return (
    <div className="screen">
      <div className="screen-header screen-header-compact">
        <h1 className="page-title">Рендер</h1>
        <span className="page-sub">
          <b>{summary.total}</b> клипов · <b>{summary.active}</b> в работе · <b>{summary.done}</b> готовы
          {summary.failed > 0 && <> · <span style={{ color: 'var(--err)' }}><b>{summary.failed}</b> ошибок</span></>}
        </span>
        <div className="grow" />
        <div className="screen-header-actions renders-toolbar">
          {someSelected && (
            <button
              className="btn btn-ghost small renders-toolbar-delete"
              disabled={deleting}
              onClick={bulkDelete}
            >
              {Icons.trash} удалить ({selected.size})
            </button>
          )}
          <select
            className={'select ' + (tab === 'failed' && summary.failed > 0 ? 'select-warn' : '')}
            value={tab}
            onChange={(e) => setTab(e.target.value)}
            title="фильтр по статусу"
          >
            <option value="all">все клипы · {summary.total}</option>
            <option value="active">в очереди / рендер · {summary.active}</option>
            <option value="done">готовые · {summary.done}</option>
            <option value="failed">ошибки · {summary.failed}</option>
            <option value="draft">черновики · {summary.draft}</option>
          </select>
        </div>
      </div>

      {items.length > 0 && (
        <div className="renders-bulk-bar">
          <label className="renders-check-all">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} />
            <span>выбрать все ({items.length})</span>
          </label>
        </div>
      )}

      {items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">◇</div>
          <div className="empty-title">
            {tab === 'all' ? 'нет клипов' : tab === 'active' ? 'очередь пуста' : tab === 'done' ? 'нет готовых' : tab === 'failed' ? 'нет ошибок' : 'нет черновиков'}
          </div>
          <div className="empty-sub">
            {tab === 'all' ? 'создай клип в транскрипте эпизода' : 'будут показаны здесь'}
          </div>
        </div>
      ) : (
        <div className="renders-list">
          {items.map((c) => (
            <ClipRow
              key={c.id}
              c={c}
              checked={selected.has(c.id)}
              onToggle={() => toggleOne(c.id)}
              navigate={navigate}
              reload={load}
              onDelete={() => deleteIds([c.id])}
              deleting={deleting}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ClipRow({ c, checked, onToggle, navigate, reload, onDelete, deleting }) {
  const dur = c.t_end != null ? Number(c.t_end) - Number(c.t_start) : null;
  const st = c.display_status || c.status || 'draft';
  const thumbSrc = c.thumbnail_url || api.clipThumbnailUrl(c.id);
  const job = c.render_job;

  return (
    <article className={'render-card render-status-' + st + (checked ? ' render-card-selected' : '')}>
      <label className="render-check" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" checked={checked} onChange={onToggle} />
      </label>
      <div className="render-thumb">
        <VideoThumb aspect="9/16" src={thumbSrc} label={dur ? formatMs(dur) : ''} />
        {st === 'rendering' && <span className="badge badge-pulse">● рендер</span>}
        {st === 'queued' && <span className="badge badge-mute">в очереди</span>}
        {(st === 'done' || c.rendered_ready) && <span className="badge" style={{ background: 'var(--ok)', color: '#fff' }}>✓ готов</span>}
        {st === 'failed' && <span className="badge" style={{ background: 'var(--err)', color: '#fff' }}>✗ ошибка</span>}
        {st === 'draft' && !c.rendered_ready && <span className="badge badge-mute">черновик</span>}
        {st === 'published' && <span className="badge" style={{ background: 'var(--sage)', color: '#fff' }}>опубл.</span>}
      </div>

      <div className="render-body">
        <div className="render-row">
          <div className="serif" style={{ fontSize: 18 }}>{c.title || '—'}</div>
          {c.title_ru && <div className="muted">{c.title_ru}</div>}
          {c.episode_title && (
            <div className="muted small">{c.episode_title_ru || c.episode_title}</div>
          )}
        </div>

        <div className="render-meta small muted">
          {c.t_start != null && <span>{formatHms(c.t_start)}</span>}
          {dur != null && <><span>·</span><span>{formatMs(dur)}</span></>}
          {c.last_edited_at && <><span>·</span><span>изменён {relativeTime(c.last_edited_at)}</span></>}
          {job?.duration_seconds != null && <><span>·</span><span>рендер {job.duration_seconds.toFixed(1)}с</span></>}
        </div>

        {job?.error_message && (
          <div className="render-err">
            <div className="render-err-title">⚠ {job.error_message}</div>
            {job.ffmpeg_log_tail && (
              <details>
                <summary className="muted small">ffmpeg лог</summary>
                <pre className="render-log">{job.ffmpeg_log_tail}</pre>
              </details>
            )}
          </div>
        )}

        <div className="render-actions">
          <button className="btn btn-outline small" onClick={() => navigate(`/clip/${c.id}`)}>
            в редактор
          </button>
          {c.rendered_ready && (
            <a className="btn btn-dark small" href={api.clipPreviewUrl(c.id)} target="_blank" rel="noreferrer">
              скачать MP4
            </a>
          )}
          {job?.status === 'failed' && (
            <button className="btn btn-publish small" onClick={() => api.retryRender(job.id).then(reload)}>
              ↻ повторить рендер
            </button>
          )}
          {job?.status === 'queued' && (
            <button className="btn btn-ghost small" onClick={() => api.cancelRender(job.id).then(reload)}>
              отменить
            </button>
          )}
          {!c.rendered_ready && st === 'draft' && (
            <button className="btn btn-publish small" onClick={() => api.renderClip(c.id).then(reload)}>
              в очередь
            </button>
          )}
          <button
            className="btn btn-ghost small"
            disabled={deleting}
            onClick={onDelete}
            title="удалить клип"
          >
            {Icons.trash}
          </button>
        </div>
      </div>
    </article>
  );
}
